import React from "react";
import { FeaturePageShell } from "./FeaturePageShell";
import hero from "./assets/earning-model/header.jpg";
import revenueSplit from "./assets/earning-model/renum1.jpg";
import perpetualRevenue from "./assets/earning-model/renum2.jpg";
import smartEscrow from "./assets/earning-model/renum3.jpg";
import commonPool from "./assets/earning-model/renum4.jpg";
import wallet from "./assets/earning-model/renum5.jpg";
import "./EarningModelPage.css";

const sections = [
  {
    title: "The YouTube Revolution: Monetize Together, Not Alone",
    subtitle: "Weavevo transforms video economics by splitting YouTube revenue between the channel creator and every contributing Weaver.",
    image: revenueSplit,
    points: [
      {
        title: "Ending creator asymmetry",
        text: "Traditionally, only the YouTube channel owner collects advertising revenue; with Weavevo, on-the-ground Weavers receive their direct share of the proceeds generated by the published video."
      },
      {
        title: "Native .weave provenance",
        text: "Every scene integrated into the master cut retains cryptographic attribution and author provenance, ensuring undisputed, automated royalty routing."
      },
      {
        title: "Persistent passive earnings",
        text: "As the video accumulates views and watch time over months and years on YouTube, ad revenue streams continuously into each contributor's balance."
      }
    ]
  },
  {
    title: "Perpetual Revenue: A Living Video That Never Sleeps",
    subtitle: "Powered by a persistent single URL and continuous scene updates on YouTube, engagement never decays: ad revenues stream into both creator and Weaver wallets indefinitely.",
    image: perpetualRevenue,
    points: [
      {
        title: "Algorithmic flywheel",
        text: "Hot-swapping fresh scenes without altering the YouTube video ID constantly reactivates recommendation algorithms, compounding views and watch time without sacrificing search rankings or engagement history."
      },
      {
        title: "Shared perpetual royalties",
        text: "As long as the video continues to gather views on YouTube, ad revenue is systematically calculated, split, and credited month after month directly to each contributor’s wallet."
      },
      {
        title: "Footage as an appreciating asset",
        text: "Completing a capture slot is no longer a one-time transaction; it transforms field footage into an active editorial stake that yields recurring returns for Weavers years down the line."
      },
      {
        title: "Zero metric reset",
        text: "Unlike legacy re-uploads that wipe analytics and reset view counts to zero, the .weave container compounds on accumulated engagement to maximize ongoing lifetime yield for the entire collective."
      }
    ]
  },
  {
    title: "Instant Bounties & Smart Escrow: Secure Every Captured Shot",
    subtitle: "Guaranteed capture bounties unlocked immediately upon slot approval.",
    image: smartEscrow,
    points: [
      {
        title: "Pre-funded Smart Escrow",
        text: "When publishing a paid project, the creator pre-funds 100% of the slot rewards directly from their wallet, ensuring Weavers that their payout is locked and guaranteed before shooting."
      },
      {
        title: "One-click instant payouts",
        text: "The moment a Lead Creator or Co-Creator approves a submitted rush for an open slot, the funds are irreversibly credited to the Weaver’s balance."
      },
      {
        title: "Dual collaboration modes",
        text: "Creators can toggle between volunteer community participation (Free) or allocate fixed monetary bounties (My wallet) across specific capture missions."
      }
    ]
  },
  {
    title: "The Common Pool Model: Earn in Proportion to Narrative Impact",
    subtitle: "Transparent, proportional revenue redistribution based on your exact contribution to the story.",
    image: commonPool,
    points: [
      {
        title: "Real-time transparency via the \"Weave’s Wallet\"",
        text: "Every production features an open financial dashboard detailing real-time engagement KPIs (views, likes, comments) and the total shared pool."
      },
      {
        title: "Mathematical percentage breakdown",
        text: "Weavers can inspect their exact revenue share (e.g., 33.3% of the collective pool) and verify their earned amounts slot by slot."
      },
      {
        title: "Valuing ground-level capture",
        text: "As the project gains traction, every contributor who provided a missing angle is rewarded equitably alongside the project owner."
      }
    ]
  },
  {
    title: "Integrated In-App Wallet: Real-Time Tracking & Frictionless Cash-Outs",
    subtitle: "Manage your financial flows, separate your creative roles, and withdraw earnings from €10.",
    image: wallet,
    points: [
      {
        title: "Unified earnings ledger (\"Total gain\")",
        text: "Your wallet consolidates all cash streams into a single balance: YouTube ad splits, creator project earnings, and rewards paid out as a Weaver."
      },
      {
        title: "Role-based segmentation (As Creator vs. As Weaver)",
        text: "Switch between roles in one tap to isolate earnings from your own initiated Weaves or track field payouts across Published, Paid, and Pending statuses."
      },
      {
        title: "Direct withdrawals",
        text: "Securely transfer your settled balance to your linked PayPal or verified bank account the moment you reach the €10 payout threshold"
      }
    ]
  }
];

export default function EarningModelPage() {
  return (
    <FeaturePageShell
      pageTitle="Earning model"
      currentPath="/earning-model"
      heroTitle="Stop creating alone. Share the success of every video."
      heroText="Turn your camera shots into recurring revenue. Join global creators on collective stories, track verified earnings live, and get paid for your creative footprint."
      heroImage={hero}
      heroAlt="Stop creating alone. Share the success of every video."
      heroClassName="fp-earningModelPage"
      showFinalCta={false}
    >
      {sections.map((section, index) => (
        <section className={`em-section ${index % 2 ? "is-reverse" : ""}`} key={section.title}>
          <div className="em-shell em-sectionGrid">
            <div className="em-visual">
              <div className="em-visualGlow" aria-hidden="true" />
              <img src={section.image} alt="" loading="lazy" />
            </div>

            <div className="em-copy">
              <h2>{section.title}</h2>
              <p className="em-lead">{section.subtitle}</p>
              <div className="em-points">
                {section.points.map((point) => (
                  <article key={point.title}>
                    <div>
                      <h3>{point.title}</h3>
                      <p>{point.text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}
    </FeaturePageShell>
  );
}
